import { useState, useEffect, useRef } from 'react';

import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { storageApi, StorageBucket, StorageFile, BucketPolicy } from '../../../../lib/api/supabase';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Switch } from '../../../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  FolderOpen,
  File,
  Trash2,
  Upload,
  ChevronRight,
  ArrowLeft,
  FolderPlus,
  Plus,
  ChevronDown,
  Settings,
  FolderX,
  Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface StorageTabProps {
  projectId: string;
}

export function StorageTab({ projectId }: StorageTabProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [buckets, setBuckets] = useState<StorageBucket[]>([]);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create bucket modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBucketName, setNewBucketName] = useState('');
  const [newBucketPublic, setNewBucketPublic] = useState(false);
  const [restrictFileSize, setRestrictFileSize] = useState(false);
  const [fileSizeLimit, setFileSizeLimit] = useState('50'); // MB
  const [restrictMimeTypes, setRestrictMimeTypes] = useState(false);
  const [allowedMimeTypes, setAllowedMimeTypes] = useState('');

  // Bucket settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsPublic, setSettingsPublic] = useState(false);
  const [settingsRestrictFileSize, setSettingsRestrictFileSize] = useState(false);
  const [settingsFileSizeLimit, setSettingsFileSizeLimit] = useState('50'); // MB
  const [settingsRestrictMimeTypes, setSettingsRestrictMimeTypes] = useState(false);
  const [settingsAllowedMimeTypes, setSettingsAllowedMimeTypes] = useState('');

  // Confirmation modal state
  const [showDeleteBucketConfirm, setShowDeleteBucketConfirm] = useState(false);
  const [showEmptyBucketConfirm, setShowEmptyBucketConfirm] = useState(false);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [emptying, setEmptying] = useState(false);

  // Policies modal state
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const [policies, setPolicies] = useState<BucketPolicy[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  useEffect(() => {
    if (token) {
      loadBuckets();
    }
  }, [projectId, token]);

  useEffect(() => {
    if (token && selectedBucket) {
      loadFiles();
    }
  }, [selectedBucket, currentPath, token]);

  const loadBuckets = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await storageApi.listBuckets(token, projectId);
      setBuckets(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load buckets: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    if (!token || !selectedBucket) return;
    setLoading(true);
    try {
      const prefix = currentPath ? `${currentPath}/` : undefined;
      const data = await storageApi.listFiles(token, projectId, selectedBucket, prefix);
      setFiles(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load files: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicies = async (bucketName: string) => {
    if (!token) return;
    setLoadingPolicies(true);
    try {
      const data = await storageApi.getBucketPolicies(token, projectId, bucketName);
      setPolicies(data);
      setShowPoliciesModal(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load policies: ${errorMessage}`);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const handleBucketClick = (bucketName: string) => {
    setSelectedBucket(bucketName);
    setCurrentPath('');
    setFiles([]);
  };

  const handleBackToBuckets = () => {
    setSelectedBucket(null);
    setCurrentPath('');
    setFiles([]);
  };

  const handleFolderClick = (folderName: string) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const handleNavigateUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !token || !selectedBucket) return;

    setUploading(true);
    try {
      const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;

      // Step 1: Get presigned upload URL from backend
      const { signedUrl } = await storageApi.getPresignedUploadUrl(
        token,
        projectId,
        selectedBucket,
        filePath
      );

      // Step 2: Upload file directly to Supabase (no base64, no backend proxy)
      await storageApi.uploadToPresignedUrl(signedUrl, file);

      toast.success(`Uploaded ${file.name}`);
      await loadFiles();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';

      // Check for file size limit error
      if (errorMessage.includes('Payload too large') || errorMessage.includes('exceeded')) {
        toast.error('File too large for this bucket. Check the bucket\'s file size limit.');
      } else {
        toast.error(`Failed to upload: ${errorMessage}`);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const confirmDeleteFile = (fileName: string) => {
    setFileToDelete(fileName);
    setShowDeleteFileConfirm(true);
  };

  const handleDeleteFile = async () => {
    if (!token || !selectedBucket || !fileToDelete) return;

    setDeleting(true);
    try {
      const filePath = currentPath ? `${currentPath}/${fileToDelete}` : fileToDelete;
      await storageApi.deleteFile(token, projectId, selectedBucket, filePath);
      toast.success(`Deleted ${fileToDelete}`);
      setShowDeleteFileConfirm(false);
      setFileToDelete(null);
      await loadFiles();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to delete: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateBucket = async () => {
    if (!token || !newBucketName.trim()) return;

    setCreating(true);
    try {
      const options: {
        name: string;
        public?: boolean;
        fileSizeLimit?: number;
        allowedMimeTypes?: string[];
      } = {
        name: newBucketName.trim(),
        public: newBucketPublic,
      };

      if (restrictFileSize && fileSizeLimit) {
        const sizeInBytes = parseFloat(fileSizeLimit) * 1024 * 1024; // Convert MB to bytes
        if (!isNaN(sizeInBytes) && sizeInBytes > 0) {
          options.fileSizeLimit = Math.round(sizeInBytes);
        }
      }

      if (restrictMimeTypes && allowedMimeTypes.trim()) {
        options.allowedMimeTypes = allowedMimeTypes
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t);
      }

      await storageApi.createBucket(token, projectId, options);
      toast.success(`Bucket "${newBucketName}" created successfully`);
      setShowCreateModal(false);
      resetCreateForm();
      await loadBuckets();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      toast.error(`Failed to create bucket: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setNewBucketName('');
    setNewBucketPublic(false);
    setRestrictFileSize(false);
    setFileSizeLimit('50');
    setRestrictMimeTypes(false);
    setAllowedMimeTypes('');
  };

  const handleDeleteBucket = async () => {
    if (!token || !selectedBucket) return;

    setDeleting(true);
    try {
      // First try to empty the bucket
      try {
        await storageApi.emptyBucket(token, projectId, selectedBucket);
      } catch {
        // Bucket might already be empty, continue with deletion
      }

      // Then delete the bucket
      await storageApi.deleteBucket(token, projectId, selectedBucket);
      toast.success(`Bucket "${selectedBucket}" deleted successfully`);
      setShowDeleteBucketConfirm(false);
      handleBackToBuckets();
      await loadBuckets();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
      toast.error(`Failed to delete bucket: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleEmptyBucket = async () => {
    if (!token || !selectedBucket) return;

    setEmptying(true);
    try {
      await storageApi.emptyBucket(token, projectId, selectedBucket);
      toast.success(`Bucket "${selectedBucket}" emptied successfully`);
      setShowEmptyBucketConfirm(false);
      // Reset to root path and clear files immediately since bucket is now empty
      setCurrentPath('');
      setFiles([]);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
      toast.error(`Failed to empty bucket: ${errorMessage}`);
    } finally {
      setEmptying(false);
    }
  };

  const getCurrentBucket = () => {
    return buckets.find(b => b.name === selectedBucket);
  };

  const openSettingsModal = () => {
    const bucket = getCurrentBucket();
    if (bucket) {
      setSettingsPublic(bucket.public);
      // File size limit: convert bytes to MB for display
      const hasFileSizeLimit = bucket.file_size_limit !== undefined && bucket.file_size_limit !== null;
      setSettingsRestrictFileSize(hasFileSizeLimit);
      setSettingsFileSizeLimit(hasFileSizeLimit ? String(Math.round(bucket.file_size_limit! / 1024 / 1024)) : '50');
      setSettingsRestrictMimeTypes(!!bucket.allowed_mime_types && bucket.allowed_mime_types.length > 0);
      setSettingsAllowedMimeTypes(bucket.allowed_mime_types?.join(', ') || '');
    }
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!token || !selectedBucket) return;

    setSaving(true);
    try {
      const options: {
        public?: boolean;
        fileSizeLimit?: number | null;
        allowedMimeTypes?: string[] | null;
      } = {
        public: settingsPublic,
      };

      if (settingsRestrictFileSize && settingsFileSizeLimit) {
        const sizeInBytes = parseFloat(settingsFileSizeLimit) * 1024 * 1024; // Convert MB to bytes
        if (!isNaN(sizeInBytes) && sizeInBytes > 0) {
          options.fileSizeLimit = Math.round(sizeInBytes);
        }
      } else {
        options.fileSizeLimit = null; // Remove restriction
      }

      if (settingsRestrictMimeTypes && settingsAllowedMimeTypes.trim()) {
        options.allowedMimeTypes = settingsAllowedMimeTypes
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t);
      } else {
        options.allowedMimeTypes = null; // Remove restriction
      }

      await storageApi.updateBucket(token, projectId, selectedBucket, options);
      toast.success('Bucket settings updated successfully');
      setShowSettingsModal(false);
      await loadBuckets(); // Refresh bucket data
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
      toast.error(`Failed to update bucket settings: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !selectedBucket) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          <span>Loading storage...</span>
        </div>
      </div>
    );
  }

  // Bucket list view
  if (!selectedBucket) {
    return (
      <div className={cn("space-y-4", isMobile && "space-y-3")}>
        {/* Header - compact on mobile */}
        {isMobile ? (
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">File Storage</h3>
            <div className="flex items-center gap-1.5">
              <button
                onClick={loadBuckets}
                className="p-2 hover:bg-muted rounded-md transition-colors"
              >
                <FolderOpen className="h-4 w-4" />
              </button>
              <Button size="sm" onClick={() => setShowCreateModal(true)} className="h-8">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">File Storage</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Manage files stored in your OptiDev Cloud buckets
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={loadBuckets} variant="outline" size="sm">
                Refresh
              </Button>
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New bucket
              </Button>
            </div>
          </div>
        )}

        {buckets.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-3 rounded-full bg-muted/50 mb-3">
                <FolderPlus className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No buckets yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Create a bucket to store images, videos, and documents
              </p>
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New bucket
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {buckets.map((bucket) => (
              <div
                key={bucket.id}
                className="rounded-lg border border-border/60 p-4 cursor-pointer hover:border-border transition-colors"
                onClick={() => handleBucketClick(bucket.name)}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <FolderOpen className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{bucket.name}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        bucket.public
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {bucket.public ? 'Public' : 'Private'}
                      </span>
                      {bucket.file_size_limit && (
                        <span className="text-xs text-muted-foreground">
                          · Max {formatFileSize(bucket.file_size_limit)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Bucket Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className={cn(
            "sm:max-w-md",
            isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
          )}>
            <DialogHeader>
              <DialogTitle>Create file bucket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Bucket Name */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Bucket name
                  <span className="text-muted-foreground font-normal ml-2">
                    Cannot be changed after creation
                  </span>
                </label>
                <Input
                  placeholder="Enter bucket name"
                  value={newBucketName}
                  onChange={(e) => setNewBucketName(e.target.value)}
                />
              </div>

              {/* Public bucket toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium text-sm">Public bucket</div>
                  <div className="text-xs text-muted-foreground">
                    Allow anyone to read objects without authorization
                  </div>
                </div>
                <Switch
                  checked={newBucketPublic}
                  onCheckedChange={setNewBucketPublic}
                />
              </div>

              {/* File size limit toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium text-sm">Restrict file size</div>
                  <div className="text-xs text-muted-foreground">
                    Set a maximum file size for uploads
                  </div>
                </div>
                <Switch
                  checked={restrictFileSize}
                  onCheckedChange={setRestrictFileSize}
                />
              </div>

              {/* File size input */}
              {restrictFileSize && (
                <div className="pl-4 border-l-2 border-muted">
                  <label className="text-sm font-medium mb-1 block">
                    Maximum file size (MB)
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={5000}
                    placeholder="50"
                    value={fileSizeLimit}
                    onChange={(e) => setFileSizeLimit(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Default is 50 MB if not specified
                  </p>
                </div>
              )}

              {/* Restrict MIME types toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium text-sm">Restrict MIME types</div>
                  <div className="text-xs text-muted-foreground">
                    Allow only certain types of files to be uploaded
                  </div>
                </div>
                <Switch
                  checked={restrictMimeTypes}
                  onCheckedChange={setRestrictMimeTypes}
                />
              </div>

              {/* MIME types input */}
              {restrictMimeTypes && (
                <div className="pl-4 border-l-2 border-muted">
                  <label className="text-sm font-medium mb-1 block">
                    Allowed MIME types (comma-separated)
                  </label>
                  <Input
                    placeholder="image/png, image/jpeg, application/pdf"
                    value={allowedMimeTypes}
                    onChange={(e) => setAllowedMimeTypes(e.target.value)}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateBucket}
                disabled={creating || !newBucketName.trim()}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // File list view
  return (
    <div className={cn("space-y-4", isMobile && "space-y-3")}>
      {/* Header - compact on mobile */}
      {isMobile ? (
        <div className="space-y-2">
          {/* Breadcrumb row */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={handleBackToBuckets}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-medium truncate max-w-24">{selectedBucket}</span>
            {currentPath && currentPath.split('/').map((part, index) => (
              <span key={index} className="flex items-center gap-1.5 flex-shrink-0">
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium">{part}</span>
              </span>
            ))}
          </div>
          {/* Actions row */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {files.length} {files.length === 1 ? 'item' : 'items'}
            </span>
            <div className="flex items-center gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-8 px-2.5"
              >
                <Upload className="h-3.5 w-3.5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 px-2.5">
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => selectedBucket && loadPolicies(selectedBucket)}>
                    <Shield className="h-4 w-4 mr-2" />
                    Policies
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openSettingsModal}>
                    <Settings className="h-4 w-4 mr-2" />
                    Bucket settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowEmptyBucketConfirm(true)}>
                    <FolderX className="h-4 w-4 mr-2" />
                    Empty bucket
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setShowDeleteBucketConfirm(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete bucket
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <button
                onClick={handleBackToBuckets}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Buckets
              </button>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{selectedBucket}</span>
              {currentPath && currentPath.split('/').map((part, index) => (
                <span key={index} className="flex items-center gap-2">
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">{part}</span>
                </span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {files.length} {files.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-1" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => selectedBucket && loadPolicies(selectedBucket)}
              disabled={loadingPolicies}
            >
              <Shield className="h-4 w-4 mr-1" />
              Policies
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={openSettingsModal}>
                  <Settings className="h-4 w-4 mr-2" />
                  Bucket settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowEmptyBucketConfirm(true)}>
                  <FolderX className="h-4 w-4 mr-2" />
                  Empty bucket
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteBucketConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete bucket
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Navigation up button */}
      {currentPath && (
        <button
          onClick={handleNavigateUp}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      )}

      {/* Files list */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
            <span>Loading files...</span>
          </div>
        </div>
      ) : files.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="p-3 rounded-full bg-muted/50 mb-3">
              <File className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium mb-1">No files yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Upload files to this {currentPath ? 'folder' : 'bucket'}
            </p>
            <Button size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-1" />
              Upload file
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => {
            // Folders in Supabase Storage have id: null and metadata: null
            const isFolder = file.id === null;
            const displayName = file.name;

            return (
              <div
                key={file.id || file.name}
                className={`rounded-lg border border-border/60 p-4 flex items-center justify-between hover:border-border transition-colors ${
                  isFolder ? 'cursor-pointer' : ''
                }`}
                onClick={isFolder ? () => handleFolderClick(displayName) : undefined}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${isFolder ? 'bg-primary/10' : 'bg-muted'}`}>
                    {isFolder ? (
                      <FolderOpen className="w-4 h-4 text-primary" />
                    ) : (
                      <File className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{displayName}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      {!isFolder && (
                        <>
                          <span>{formatFileSize(file.metadata?.size)}</span>
                          <span>·</span>
                        </>
                      )}
                      <span>{formatDate(file.updated_at)}</span>
                    </div>
                  </div>
                </div>
                {isFolder ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDeleteFile(file.name);
                    }}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bucket Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className={cn(
          "sm:max-w-md",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle>Bucket settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Bucket Name (read-only) */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Bucket name
                <span className="text-muted-foreground font-normal ml-2">
                  Cannot be changed
                </span>
              </label>
              <Input
                value={selectedBucket || ''}
                disabled
                className="bg-muted text-muted-foreground"
              />
            </div>

            {/* Public bucket toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium text-sm">Public bucket</div>
                <div className="text-xs text-muted-foreground">
                  Allow anyone to read objects without authorization
                </div>
              </div>
              <Switch
                checked={settingsPublic}
                onCheckedChange={setSettingsPublic}
              />
            </div>

            {/* File size limit toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium text-sm">Restrict file size</div>
                <div className="text-xs text-muted-foreground">
                  Set a maximum file size for uploads
                </div>
              </div>
              <Switch
                checked={settingsRestrictFileSize}
                onCheckedChange={setSettingsRestrictFileSize}
              />
            </div>

            {/* File size input */}
            {settingsRestrictFileSize && (
              <div className="pl-4 border-l-2 border-muted">
                <label className="text-sm font-medium mb-1 block">
                  Maximum file size (MB)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={5000}
                  placeholder="50"
                  value={settingsFileSizeLimit}
                  onChange={(e) => setSettingsFileSizeLimit(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Default is 50 MB if not specified
                </p>
              </div>
            )}

            {/* Restrict MIME types toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium text-sm">Restrict MIME types</div>
                <div className="text-xs text-muted-foreground">
                  Allow only certain types of files to be uploaded
                </div>
              </div>
              <Switch
                checked={settingsRestrictMimeTypes}
                onCheckedChange={setSettingsRestrictMimeTypes}
              />
            </div>

            {/* MIME types input */}
            {settingsRestrictMimeTypes && (
              <div className="pl-4 border-l-2 border-muted">
                <label className="text-sm font-medium mb-1 block">
                  Allowed MIME types (comma-separated)
                </label>
                <Input
                  placeholder="image/png, image/jpeg, application/pdf"
                  value={settingsAllowedMimeTypes}
                  onChange={(e) => setSettingsAllowedMimeTypes(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Bucket Confirmation */}
      <AlertDialog open={showDeleteBucketConfirm} onOpenChange={setShowDeleteBucketConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bucket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete bucket "{selectedBucket}"? This will delete all files in the bucket and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBucket}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty Bucket Confirmation */}
      <AlertDialog open={showEmptyBucketConfirm} onOpenChange={setShowEmptyBucketConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty bucket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to empty bucket "{selectedBucket}"? All files will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={emptying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEmptyBucket}
              disabled={emptying}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {emptying ? 'Emptying...' : 'Empty'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete File Confirmation */}
      <AlertDialog open={showDeleteFileConfirm} onOpenChange={(open) => {
        setShowDeleteFileConfirm(open);
        if (!open) setFileToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{fileToDelete}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFile}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Policies Modal */}
      <Dialog open={showPoliciesModal} onOpenChange={setShowPoliciesModal}>
        <DialogContent className={cn(
          "sm:max-w-lg",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              RLS Policies for {selectedBucket}
            </DialogTitle>
            <DialogDescription>
              Row Level Security policies control who can access files in this bucket.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {policies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No policies configured</p>
                <p className="text-sm mt-1">
                  This bucket has no RLS policies. Files may not be accessible.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {policies.map((policy, index) => (
                  <div key={index} className="border border-border/40 rounded-lg p-3 hover:bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        policy.command === 'DELETE' ? 'bg-destructive/10 text-destructive' :
                        'bg-primary/10 text-primary'
                      }`}>
                        {policy.command}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                        {policy.appliedTo}
                      </span>
                      <span className="text-sm font-medium">{policy.name}</span>
                    </div>
                    {policy.definition && (
                      <div className="bg-muted/50 rounded p-2 overflow-x-auto">
                        <code className="text-xs text-muted-foreground whitespace-pre-wrap break-all">
                          {policy.definition}
                        </code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              To modify policies, ask the AI assistant.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPoliciesModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
